import { zodResolver } from "@hookform/resolvers/zod";
import {
    Box,
    CircularProgress,
    Divider,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { t } from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { useToasts } from "../../hooks/useToasts";
import toast from "react-hot-toast";

import { useChannelById } from "../../services/channelsService";
import { useChannelsFilteringData } from "../../services/channelsService";
import { convertFlatListFromBackendToTranslatedOptionObject, boolToString  } from "../../helpers/methods";
import { useUpdateChannelMutation } from "../../services/channelsService";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { ChannelDocuments } from "./ChannelDocumets";

export const channnelPassportFormSchema = z
    .object({
        channel_higher_name: z.string().optional().nullable(),
        channel_type: z.string().optional().nullable(),
        channel_person: z.string().optional().nullable(),
        channel_phone: z.string().optional().nullable(),
        channel_mail: z.preprocess(
            (val) => (val === "" ? undefined : val),
            z.string().email(t("form.validations.incorrect_email")).optional().nullable(),
        ),
        channel_nip: z.string().optional().nullable(),
        channel_regon: z.string().optional().nullable(),
        channel_legal: z.string().optional().nullable(),
        channel_note: z.string().optional().nullable(),
    })
    .superRefine((data, ctx) => {
        // Phone validation
        const phoneRegex = /^\+48\s?\d{3}\s?\d{3}\s?\d{3}$/;
        if (data.channel_phone && !phoneRegex.test(data.channel_phone)) {
            ctx.addIssue({
                message: t("form.validations.incorrect_phone"),
                code: z.ZodIssueCode.custom,
            });
        }

        // NIP validation (10 digits)
        const nipRegex = /^\d{10}$/;
        if (data.channel_nip && !nipRegex.test(data.channel_nip)) {
            ctx.addIssue({
                message: t("form.validations.incorrect_nip"),
                code: z.ZodIssueCode.custom,
            });
        }

        // REGON validation (9 or 14 digits)
        const regonRegex = /^(\d{9}|\d{14})$/;
        if (data.channel_regon && !regonRegex.test(data.channel_regon)) {
            ctx.addIssue({
                message: t("form.validations.incorrect_regon"),
                code: z.ZodIssueCode.custom,
            });
        }
        return z.NEVER;
    });


export function ChannelPassportDialog(props) {
    const { channelId } = props;
    
    const { data: channelData, isLoading, isFetching, refetch } = useChannelById(channelId);

    const { data: filteringData } = useChannelsFilteringData()

    const title = channelData
        ? `${t("channels.channel_name")}: ${channelData.channel_name || ""}`
        : t("channels.channel_name");

    const { handleSubmit, control, formState, register, reset, setValue, watch } = useForm({
        resolver: zodResolver(channnelPassportFormSchema),
        defaultValues: {},
    });

    const { onFormErrors, onServerError } = useToasts();

    const { mutate: updateChannel } = useUpdateChannelMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("successfullyEdited")} />,
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            onServerError(error);
        },
    });


    // Update form values when channel data is loaded
    useEffect(() => {
        if (channelData) {
            reset(channelData)
        }
    }, [channelData, reset]);


    const onSubmit = (data) => {
        updateChannel({data:data, id:channelId})
    };

    if (isLoading || isFetching || !filteringData) {
        return (
            <BasicDialog
                open={props.open}
                onClose={props.onClose}
                titleAlign={"center"}
                contentAlign={"center"}
                title="Loading..."
                maxWidth={"lg"}
                showDialogActions
                alwaysFullScreen={true}
            >
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            </BasicDialog>
        );
    }

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            showTitle={false}
            title={title}
            maxWidth={"lg"}
            showDialogActions
            alwaysFullScreen={true}
            headerComponent={
                <Grid
                    container
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    sx={{ p: 2, backgroundColor: "#f5f5f5" }}
                    gap={3}
                >
                    <Typography variant={"h4"} sx={{ fontSize: "2rem", ontWeight: "bold" }}>
                        {title}
                    </Typography>
                </Grid>
            }
            
        >
            <Grid container  rowSpacing={1} columnSpacing={1} alignItems={"flex-start"} sx={{ backgroundColor: "#f5f5f5" }}>
                <Grid item xs={4.5} container rowSpacing={1}>
                    <Grid item xs={12} >
                        <TextField
                            {...register("channel_higher_name")}
                            fullWidth
                            disabled={false}
                            label={t("channels.channel_higher_name")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <ControlledSelect
                            control={control}
                            sx={{background: "white"}}
                            name="channel_type"
                            label={t("channels.channel_type")}
                            data={convertFlatListFromBackendToTranslatedOptionObject(
                                filteringData?.channel_type,
                                "value",
                                "label",
                                "channels.types",
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            {...register("channel_person")}
                            fullWidth
                            disabled={false}
                            label={t("channels.channel_person")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            {...register("channel_phone")}
                            fullWidth
                            disabled={false}
                            label={t("channels.channel_phone")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            {...register("channel_mail")}
                            fullWidth
                            disabled={false}
                            label={t("channels.channel_mail")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={4.5} container rowSpacing={1}>
                    <Grid item xs={12} >
                        <ControlledSelect
                            control={control}
                            sx={{background: "white"}}
                            name="channel_legal"
                            label={t("channels.channel_legal")}
                            data={convertFlatListFromBackendToTranslatedOptionObject(
                                filteringData?.channel_legal,
                                "value",
                                "label",
                                "channels.legals",
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            {...register("channel_nip")}
                            fullWidth
                            disabled={false}
                            label={t("channels.channel_nip")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            {...register("channel_regon")}
                            fullWidth
                            disabled={false}
                            label={t("channels.channel_regon")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                    <Grid item xs={12} >
                        <TextField
                            value={boolToString(register("active"))}
                            fullWidth
                            disabled={true}
                            label={t("channels.active")}
                            size="small"
                            sx={{background: "white"}}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={3}></Grid>
                <Grid item xs={9}>
                    <TextField
                        {...register("channel_note")}
                        fullWidth
                        disabled={false}
                        label={t("channels.channel_note")}
                        size="small"
                        multiline
                        rows={2}
                        sx={{background: "white"}}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ChannelDocuments channelId={channelId}/>
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton
                        loading={isFetching}
                        disabled={false}
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        onClick={handleSubmit(onSubmit, onFormErrors)}
                        fullWidth
                    >
                        {t("clients.passportDialogCompany.saveChanges")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
